/**
 * AI features, all routed through the server.
 *
 * The single-file build called api.anthropic.com straight from the browser,
 * which meant the API key had to ship to every client and CORS blocked it
 * anyway. Every call here goes to `/api/ai/*` instead: the key stays in the
 * server environment, and the server builds the context from the database, so
 * the model can only ever see data the signed-in user is entitled to.
 */
import api from '../lib/api.js';
import { isOffline } from '../lib/storage.js';

export class AIUnavailable extends Error {
  constructor(message) {
    super(message);
    this.name = 'AIUnavailable';
  }
}

/** Guard every entry point: AI switched off, or no server to ask. */
function assertReady(S) {
  if (S && S.aiOn === false) {
    throw new AIUnavailable('AI features are switched off in Settings → AI');
  }
  if (isOffline()) {
    throw new AIUnavailable('Sign in to use AI — the model runs on the server');
  }
}

/** Turn an API failure into something worth showing a user. */
function explain(e) {
  if (e instanceof AIUnavailable) return e;
  if (e && e.status === 429) return new AIUnavailable('Too many AI requests — give it a minute');
  if (e && e.status === 400 && /(ANTHROPIC|GEMINI)_API_KEY/i.test(e.message || '')) {
    return new AIUnavailable('No AI key is configured on the server');
  }
  return new AIUnavailable((e && e.message) || 'The AI request failed');
}

const run = async (S, fn) => {
  assertReady(S);
  try {
    return await fn();
  } catch (e) {
    throw explain(e);
  }
};

/** Morning brief across clients and pipeline. */
export const generateBrief = S => run(S, () => api.ai.brief());

/** The same day split per owner, written to be emailed to the team. */
export const generateDigest = S => run(S, () => api.ai.digest());

/** Pipeline-only read for the sales overview. */
export const generateSalesBrief = S => run(S, () => api.ai.salesBrief());

/** "What this means" — conversion, leakage, promo performance. */
export const analyse = S => run(S, () => api.ai.analyse().then(r => r.text));

/**
 * Score every client for churn risk.
 *
 * Minutes of model time, so the server runs it as a job and we poll: the
 * button can then say how far along it is instead of spinning in silence for
 * two and a half minutes with no way to tell working from hung.
 */
const POLL_MS = 1500;
const GIVE_UP_MS = 15 * 60 * 1000;

export const scoreClients = (S, onProgress = () => {}) => run(S, async () => {
  const { jobId } = await api.ai.scoreClients();
  const until = Date.now() + GIVE_UP_MS;

  for (;;) {
    await new Promise(r => setTimeout(r, POLL_MS));
    const s = await api.ai.scoreStatus(jobId);
    if (s.state === 'failed') throw new Error(s.error || 'The scan failed');
    if (s.state === 'done') return s;
    onProgress({ done: s.done || 0, total: s.total || 0 });
    if (Date.now() > until) throw new Error('The scan is taking too long — try a smaller book');
  }
});

/** Score every open deal on likelihood to close. */
export const scoreDeals = S => run(S, () => api.ai.scoreDeals());

/** Copilot. `messages` is the running [{role, content}] transcript. */
export const chat = (S, messages) => run(S, () => api.ai.chat(messages).then(r => r.reply));

/**
 * Draft outreach for a client or a deal. The server writes the result into the
 * outbox table and returns the saved row.
 */
export const draft = (S, { clientId, dealId, channel }) =>
  run(S, () => api.ai.draft({ clientId, dealId, channel: channel || 'email' }));

/** Plain English to a filter object for the client list. */
export const smartSearch = (S, q) => run(S, () => api.ai.search(q));

/** 30-day token usage by feature, for Settings → AI. */
export const usage = S => run(S, () => api.ai.usage());

/**
 * One record, read properly.
 *
 * Every other call in this file reads across the whole book. This reads the
 * row somebody is actually looking at — a lead, a deal, a customer, a trial,
 * a contact, a company, or the open board as a whole — and always answers in
 * the same shape, so one panel draws all of them.
 */
export const insight = (S, module, id) => run(S, () => api.ai.insight(module, id));
